/**
 * ImageModal.tsx
 * Enhanced image modal with zoom and pan functionality
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ZoomOut, RotateCw, Download } from 'lucide-react';
import { Button } from '@vsat/shared/components';
import { cn } from '@vsat/shared/lib/utils';

interface Props {
  src: string;
  alt: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function ImageModal({ src, alt, isOpen, onClose }: Props) {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  const handleZoomIn = () => setScale(prev => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setScale(prev => Math.max(prev / 1.2, 0.5));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const handleReset = () => {
    setScale(1);
    setRotation(0);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = src;
    link.download = alt || 'question-image';
    link.click();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm'
        onClick={onClose}
      >
        {/* Controls */}
        <div className='absolute top-4 right-4 flex gap-2 z-10'>
          <Button
            variant='secondary'
            size='sm'
            onClick={e => {
              e.stopPropagation();
              handleZoomOut();
            }}
            className='bg-white/90 hover:bg-white'
          >
            <ZoomOut className='w-4 h-4' />
          </Button>
          <Button
            variant='secondary'
            size='sm'
            onClick={e => {
              e.stopPropagation();
              handleZoomIn();
            }}
            className='bg-white/90 hover:bg-white'
          >
            <ZoomIn className='w-4 h-4' />
          </Button>
          <Button
            variant='secondary'
            size='sm'
            onClick={e => {
              e.stopPropagation();
              handleRotate();
            }}
            className='bg-white/90 hover:bg-white'
          >
            <RotateCw className='w-4 h-4' />
          </Button>
          <Button
            variant='secondary'
            size='sm'
            onClick={e => {
              e.stopPropagation();
              handleDownload();
            }}
            className='bg-white/90 hover:bg-white'
          >
            <Download className='w-4 h-4' />
          </Button>
          <Button
            variant='secondary'
            size='sm'
            onClick={onClose}
            className='bg-white/90 hover:bg-white'
          >
            <X className='w-4 h-4' />
          </Button>
        </div>

        {/* Reset button */}
        <div className='absolute bottom-4 left-1/2 transform -translate-x-1/2'>
          <Button
            variant='secondary'
            size='sm'
            onClick={e => {
              e.stopPropagation();
              handleReset();
            }}
            className='bg-white/90 hover:bg-white'
          >
            Reset View
          </Button>
        </div>

        {/* Image container */}
        <motion.div
          className='relative max-w-[90vw] max-h-[90vh] overflow-hidden'
          onClick={e => e.stopPropagation()}
        >
          <motion.img
            src={src}
            alt={alt}
            className={cn(
              'max-w-full max-h-full object-contain cursor-grab active:cursor-grabbing',
              'shadow-2xl rounded-lg'
            )}
            style={{
              transform: `scale(${scale}) rotate(${rotation}deg)`,
              transition: 'transform 0.2s ease-out',
            }}
            drag
            dragConstraints={{ left: -100, right: 100, top: -100, bottom: 100 }}
            whileDrag={{ cursor: 'grabbing' }}
          />
        </motion.div>

        {/* Image info */}
        <div className='absolute bottom-4 right-4 bg-white/90 rounded-lg px-3 py-2 text-sm text-gray-700'>
          Zoom: {Math.round(scale * 100)}% | Rotation: {rotation}°
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
